import React from 'react';
import { ChevronDown, ChevronUp, Users, ExternalLink } from 'lucide-react';
import { MatchedAccountNeedingEmployees } from './NewAccountReviewModal';
import { UserAssignmentRow, UserOption } from './UserAssignmentRow';

interface ExistingAccountRowProps {
  account: MatchedAccountNeedingEmployees;
  userAssignments: { user_id: number; commission_percentage: number }[];
  isExpanded: boolean;
  users: UserOption[];
  /**
   * R13 Task C-r: per-row "Send to House" opt-in. When checked, the
   * user_assignment editors are disabled and proceeding becomes valid
   * for this row even without configured salesmen. Mutually exclusive
   * with the "assign employees" flow.
   */
  sendToHouse: boolean;
  onToggleSendToHouse: () => void;
  onToggleExpand: () => void;
  onAddUser: () => void;
  onUpdateUser: (idx: number, field: 'user_id' | 'commission_percentage', value: number) => void;
  onRemoveUser: (idx: number) => void;
}

export const ExistingAccountReviewRow: React.FC<ExistingAccountRowProps> = ({
  account,
  userAssignments,
  isExpanded,
  users,
  sendToHouse,
  onToggleSendToHouse,
  onToggleExpand,
  onAddUser,
  onUpdateUser,
  onRemoveUser,
}) => {
  const hasValid = userAssignments.some(
    (ua) => ua.user_id > 0 && ua.commission_percentage > 0
  );
  // The row resolves the blocker if EITHER a real salesman is configured OR
  // the user has explicitly opted into routing this account's commission to
  // House. The modal aggregates this across rows to gate the proceed button.
  const isResolved = hasValid || sendToHouse;

  return (
    <div className="border rounded-lg overflow-hidden">
      <div
        className="flex items-center justify-between p-4 bg-gray-50 cursor-pointer hover:bg-gray-100"
        onClick={onToggleExpand}
      >
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{account.account_name}</p>
          <p className="text-xs text-gray-500 mt-0.5">
            Client: {account.client_name} &middot; matched from{' '}
            <span className="font-mono">{account.account_string}</span>
          </p>
        </div>
        <div className="flex items-center space-x-3">
          {sendToHouse ? (
            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded font-medium">
              Sending to House
            </span>
          ) : hasValid ? (
            <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded font-medium">
              Employees set
            </span>
          ) : (
            <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded font-medium">
              Unresolved
            </span>
          )}
          {isExpanded ? (
            <ChevronUp className="h-4 w-4 text-gray-400" />
          ) : (
            <ChevronDown className="h-4 w-4 text-gray-400" />
          )}
        </div>
      </div>

      {isExpanded && (
        <div className="p-4 space-y-3 border-t">
          {/* Per-row House override + escape hatch to the Accounts page.
              Mutually exclusive with assigning employees: ticking House
              disables the editor below to make the intent explicit. */}
          <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded p-2.5">
            <label className="flex items-center space-x-2 text-sm text-gray-800 cursor-pointer">
              <input
                type="checkbox"
                checked={sendToHouse}
                onChange={onToggleSendToHouse}
                className="rounded border-gray-300"
              />
              <span>
                Send to House &mdash; route 100% of this account's commission to the House user
              </span>
            </label>
            {/*
              R15 Task B2: use ?search=<account_name> instead of
              ?account_id=<id>. AccountList reads `search` from the URL
              and pipes it through its existing name-filter, so the
              destination page lands pre-filtered to just this account.
              account_id was previously ignored, leaving the user on an
              unfiltered paginated list.
            */}
            <a
              href={`/accounts?search=${encodeURIComponent(account.account_name)}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 whitespace-nowrap"
            >
              <ExternalLink className="h-3 w-3" />
              Assign on Accounts page
            </a>
          </div>

          {/*
            R13 CR HIGH #3: swapped `pointer-events-none` for real
            `disabled` on the child controls. `pointer-events-none`
            only blocks mouse events — keyboard Tab still reached the
            <select>/<input> inside UserAssignmentRow and users could
            mutate state that would be silently dropped at Confirm.
            The visual `opacity-50` stays as an affordance.
          */}
          <div
            className={sendToHouse ? 'opacity-50' : ''}
            aria-disabled={sendToHouse}
          >
            <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-1.5">
              <Users className="h-4 w-4" />
              <span>Assign Employees</span>
            </label>
            {userAssignments.map((ua, idx) => (
              <UserAssignmentRow
                key={idx}
                ua={ua}
                users={users}
                onUpdate={(field, value) => onUpdateUser(idx, field, value)}
                onRemove={() => onRemoveUser(idx)}
                disabled={sendToHouse}
              />
            ))}
            <button
              onClick={onAddUser}
              disabled={sendToHouse}
              aria-disabled={sendToHouse}
              className="text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              + Add Employee
            </button>
          </div>

          {!isResolved && (
            <p className="text-xs text-red-600">
              Resolve this account by either assigning at least one employee or ticking
              &ldquo;Send to House&rdquo; above before you can confirm the upload.
            </p>
          )}
        </div>
      )}
    </div>
  );
};
